// src/components/SEOHelmet.js
import React from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { SEO_CONFIG, SITE_CONFIG } from '../config';

// Apex origin derived from config — defaults must never be hand-edited
// placeholders (clones shipped with dead canonical domains that way).
const ORIGIN = `https://${SITE_CONFIG.BASE_URL.replace(/^https?:\/\//, '').replace(/^www\./, '')}`;

const SEOHelmet = ({
  title = `${SEO_CONFIG.AGENT_NAME} - Your Local Real Estate Expert | Buy, Sell, Lease Properties`,
  description = `Find your dream home with ${SEO_CONFIG.AGENT_NAME}. Expert real estate services, property search, buying, selling, and leasing assistance. Chat with our AI assistant for instant help.`,
  keywords = "real estate, homes for sale, property search, buy house, sell house, lease property, real estate agent, local realtor, property listings, home buying, home selling",
  canonical = ORIGIN,
  ogImage = `${ORIGIN}/og-image.jpg`
}) => {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      
      {/* Conversational/Chat Keywords */}
      <meta name="chat-keywords" content="ask real estate questions, property chat, home buying help, real estate assistant, talk to agent, property advice" />
      
      {/* Local SEO */}
      <meta name="geo.region" content={SEO_CONFIG.COUNTRY} />
      <meta name="geo.placename" content={`${SEO_CONFIG.CITY}, ${SEO_CONFIG.STATE}`} />
      <meta name="geo.position" content={`${SEO_CONFIG.LATITUDE};${SEO_CONFIG.LONGITUDE}`} />
      <meta name="ICBM" content={`${SEO_CONFIG.LATITUDE}, ${SEO_CONFIG.LONGITUDE}`} />
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      
      {/* Twitter */}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Structured Data for Real Estate Agent */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "RealEstateAgent",
          "name": SEO_CONFIG.AGENT_NAME,
          "telephone": SEO_CONFIG.AGENT_PHONE,
          "email": SEO_CONFIG.AGENT_EMAIL,
          "url": ORIGIN,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": SEO_CONFIG.STREET_ADDRESS,
            "addressLocality": SEO_CONFIG.CITY,
            "addressRegion": SEO_CONFIG.STATE,
            "postalCode": SEO_CONFIG.ZIP_CODE,
            "addressCountry": SEO_CONFIG.COUNTRY
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEOHelmet;